import styled from 'styled-components';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Metrics } from 'components/insights/constants';
import { formatDaysValue, formatDecimal, formatPercentage, formatWholeNumber } from 'components/insights/formatting';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

export type ProviderPerformanceRow = {
  entityName: string;
  entityId: string;
  totalDischarges: number;
  avgLos: number | null;
  readmissionRate: number | null;
  oes: number | null;
  engagementsPerPatient: number | null;
};

export const PROVIDER_DEFAULT_SORT = { id: 'totalDischarges', desc: true };

const CELL_PADDING = { meta: { padding: '16px 12px' } };
const CELL_PADDING_LEFT = { meta: { padding: '16px 12px 16px 24px' } };

export function buildProviderPerformanceRows(rollups: AnalyticsResponseRollupCollection): ProviderPerformanceRow[] {
  // Entity-level rollup: grouping with entity name preserved (metrics are aggregated).
  // The id dimension is rolled up at this level, so we build a name→id lookup from
  // the detail rows (grouping 0) where both name and id are present.
  const entityGrouping = rollups.groupings[1];
  if (!entityGrouping) return [];

  const idByName = new Map<string, string>();
  for (const row of rollups.analyticsResponse.responseData) {
    if ((row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0) {
      const name = String(row.dimensionValues[0]?.value ?? '');
      const id = String(row.dimensionValues[1]?.value ?? '');
      if (name && id && id !== 'None') idByName.set(name, id);
    }
  }

  const entityNames = entityGrouping.dimensions.getCurrentDimensionValuesByIndex(0);
  const discharges = entityGrouping.metrics.getCurrentSeries(Metrics.ID_COUNT);
  const avgLos = entityGrouping.metrics.getCurrentSeries(Metrics.LENGTH_OF_STAY_AVERAGE);
  const readmissionRates = entityGrouping.metrics.getCurrentSeries(Metrics.READMISSION_RATE);
  const oesScores = entityGrouping.metrics.getCurrentSeries(Metrics.OLIO_ENGAGEMENT_SCORE);
  const engagements = entityGrouping.metrics.getCurrentSeries(Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK);

  return entityNames
    .map((name, i) => ({
      entityName: name,
      entityId: idByName.get(name) ?? '',
      totalDischarges: discharges[i] ?? 0,
      avgLos: avgLos[i] ?? null,
      readmissionRate: readmissionRates[i] ?? null,
      oes: oesScores[i] ?? null,
      engagementsPerPatient: engagements[i] ?? null,
    }))
    .filter((row) => row.entityName);
}

export function buildProviderPerformanceColumns(
  entityHeader: string,
  onEntityClick: (entityId: string, entityName: string) => void
): ColumnDef<ProviderPerformanceRow, any>[] {
  const helper = createColumnHelper<ProviderPerformanceRow>();

  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: (info) => (
        <EntityName
          type='button'
          onClick={() => onEntityClick(info.row.original.entityId, info.row.original.entityName)}>
          {info.getValue()}
        </EntityName>
      ),
      size: 320,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    helper.accessor('totalDischarges', {
      header: 'Total Discharges',
      cell: (info) => formatWholeNumber(info.getValue()),
      size: 145,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    helper.accessor('avgLos', {
      header: 'Avg. Length of Service',
      cell: (info) => formatDaysValue(info.getValue()),
      size: 170,
      enableSorting: true,
      ...CELL_PADDING,
    }),
    helper.accessor('readmissionRate', {
      header: 'Readmission Rate (%)',
      cell: (info) => formatPercentage(info.getValue()),
      size: 160,
      enableSorting: true,
      ...CELL_PADDING,
    }),
    helper.accessor('oes', {
      header: 'Olio Engagement Score (OES)',
      cell: (info) => formatPercentage(info.getValue(), 0),
      size: 205,
      enableSorting: true,
      ...CELL_PADDING,
    }),
    helper.accessor('engagementsPerPatient', {
      header: 'Engagements Per Patient Per Week',
      cell: (info) => formatDecimal(info.getValue()),
      size: 240,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}

const EntityName = styled.button`
  background: none;
  border: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  display: block;
  width: calc(100% + 36px);
  margin: -16px -12px -16px -24px;
  padding: 16px 12px 16px 24px;
  color: ${({ theme }) => theme.colors.blue100};
  font-weight: var(--font-weight-medium);
  white-space: normal;
  overflow-wrap: anywhere;

  td:hover & {
    text-decoration: underline;
  }
`;
